import axios, {
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { localStorageGet, localStorageSet } from "../utils/localStorage";
import type { ILoggedInUser } from "./auth/auth.interfaces";
import { isInstituteScopedUser } from "../utils/userTypes";

const env =
  localStorageGet("environment") ??
  import.meta.env.VITE_ENVIRONMENT ??
  "production";
/**
 * Pre-configured instance of Axios
 */
const axiosInstance = axios.create({
  // Use environment variable or Local storage to override API base path
  baseURL: getBaseUrl(`${env}`),
});

function getBaseUrl(env: string): string {
  // Check for Vite environment variable first
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL.endsWith("/")
      ? import.meta.env.VITE_API_URL
      : `${import.meta.env.VITE_API_URL}/`;
  }

  // Fallback to hardcoded URLs based on environment
  switch (env) {
    case "development":
      return "http://localhost:4001/v1/";
    case "staging":
    case "production":
      return "http://12.108.254.108:8380/";
    default:
      return "http://12.108.254.108:8380/";
  }
}

axiosInstance.interceptors.request.use(
  (
    request: InternalAxiosRequestConfig<any>,
  ): InternalAxiosRequestConfig<any> => {
    const token = localStorageGet("token");
    if (token) {
      request!.headers!.Authorization = `Bearer ${token}`;
    }

    // Add selected institute ID for tenant-scoped calls. Institute-scoped users rely on this
    // header globally. Super admins skip it for `/institutes/by-id/...` so the path id is
    // authoritative and a mismatched toolbar selection does not yield 400 from the API.
    const selectedInstitute = localStorageGet("selectedInstitute");
    const userDetailsRaw = localStorageGet("userDetails");
    const userDetails: ILoggedInUser | null =
      userDetailsRaw && typeof userDetailsRaw === "object"
        ? (userDetailsRaw as ILoggedInUser)
        : null;
    const scopedUser = isInstituteScopedUser(userDetails);
    const isInstitutesByIdUrl =
      typeof request.url === "string" &&
      request.url.includes("/institutes/by-id/");
    const skipInstituteHeader =
      isInstitutesByIdUrl && !scopedUser;

    if (
      selectedInstitute &&
      typeof selectedInstitute === "object" &&
      "id" in selectedInstitute &&
      !skipInstituteHeader
    ) {
      const instituteId = (selectedInstitute as { id: string }).id;
      // Only add institute ID if not fetching dashboard data (which lists all institutes)
      if (
        !request.url?.includes("/dashboard") ||
        request.method?.toLowerCase() !== "get"
      ) {
        request!.headers!["X-Institute-Id"] = instituteId;
      }
    }

    return request;
  },
  (error) => Promise.reject(error),
);

/**
 * Interceptor to handle token refresh and logout on 401
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Refresh token function - makes direct API call without using React hooks
 */
const refreshToken = async (): Promise<string> => {
  const appWithMockData = import.meta.env.VITE_APP_WITH_MOCK_DATA === "true";

  if (appWithMockData) {
    // In mock mode, return the existing token (mocks don't expire)
    const token = localStorageGet("token");
    if (token) {
      return token as string;
    }
    throw new Error("No token available to refresh");
  }

  // Make direct API call to refresh endpoint
  // Use a new axios instance to avoid interceptor loop
  const refreshAxios = axios.create({
    baseURL: getBaseUrl(`${env}`),
  });

  // Add current token to refresh request
  const currentToken = localStorageGet("token");
  if (!currentToken) {
    throw new Error("No token available to refresh");
  }

  try {
    const response = await refreshAxios.post(
      "auth/refresh",
      {},
      {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      },
    );

    // Handle different response formats
    const responseData = response.data?.data || response.data;

    if (responseData?.token) {
      return responseData.token;
    }
    if (responseData?.jwtToken) {
      return responseData.jwtToken;
    }
    if (typeof responseData === "string") {
      return responseData;
    }

    throw new Error("Invalid refresh token response");
  } catch (error: any) {
    // If refresh endpoint doesn't exist or fails, throw error
    throw new Error(error?.response?.data?.message || "Token refresh failed");
  }
};

function getApplicationCodeFromBody(res: AxiosResponse): number | undefined {
  const raw = res.data;
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return undefined;
  }
  const top = raw as Record<string, unknown>;
  if ("code" in top && top.code != null) {
    const n = Number(top.code);
    return Number.isFinite(n) ? n : undefined;
  }
  const inner = top.data;
  if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
    const nested = inner as Record<string, unknown>;
    if ("code" in nested && nested.code != null) {
      const n = Number(nested.code);
      return Number.isFinite(n) ? n : undefined;
    }
  }
  return undefined;
}

axiosInstance.interceptors.response.use(
  async (res) => {
    // TODO: Naman - align success-path parsing with OpenAPI when contract is finalized (flatten dual shapes: flat code vs nested data.code).
    const status = getApplicationCodeFromBody(res);
    const response = { ...res } as AxiosResponse;

    if (Number(status) === 401) {
      const originalRequest = response.config;

      // Skip refresh for auth endpoints to avoid infinite loop
      if (
        originalRequest.url?.includes("/signin") ||
        originalRequest.url?.includes("/refresh")
      ) {
        return Promise.reject(new Error("Authentication failed"));
      }

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const token = localStorageGet("token");
          if (token) {
            // Attempt to refresh token
            const newToken = await refreshToken();

            if (newToken) {
              // Update token in localStorage
              localStorageSet("token", newToken);

              // Update userDetails if it contains token
              const userDetails = localStorageGet("userDetails");
              if (userDetails && typeof userDetails === "object") {
                const updatedUserDetails = {
                  ...userDetails,
                  token: newToken,
                  jwtToken: newToken,
                };
                localStorageSet("userDetails", updatedUserDetails);
              }

              // Update the original request with new token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;

              // Process queued requests with new token
              processQueue(null, newToken);

              // Retry the original request
              return axiosInstance(originalRequest);
            }
          }

          // If refresh failed or no token, clear and redirect
          throw new Error("Token refresh failed");
        } catch (error) {
          // Refresh failed - clear tokens and redirect to login
          processQueue(error, null);
          localStorage.removeItem("token");
          localStorage.removeItem("userDetails");
          window.location.href = "/";
        } finally {
          isRefreshing = false;
        }
      } else {
        // Queue the request while token is being refreshed
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }
    }

    return response;
  },
  async (err) => {
    const originalRequest = err.config;

    // Handle 401 errors from response interceptor
    if (err.response?.status === 401) {
      // Skip refresh for auth endpoints
      if (
        originalRequest?.url?.includes("/signin") ||
        originalRequest?.url?.includes("/refresh")
      ) {
        localStorage.removeItem("token");
        localStorage.removeItem("userDetails");
        window.location.href = "/";
        return Promise.reject(err);
      }

      // Check if this is a retry (already attempted refresh)
      if (originalRequest._retry) {
        localStorage.removeItem("token");
        localStorage.removeItem("userDetails");
        window.location.href = "/";
        return Promise.reject(err);
      }

      // Mark request as retried
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const token = localStorageGet("token");
          if (token) {
            // Attempt to refresh token
            const newToken = await refreshToken();

            if (newToken) {
              // Update token in localStorage
              localStorageSet("token", newToken);

              // Update userDetails if it contains token
              const userDetails = localStorageGet("userDetails");
              if (userDetails && typeof userDetails === "object") {
                const updatedUserDetails = {
                  ...userDetails,
                  token: newToken,
                  jwtToken: newToken,
                };
                localStorageSet("userDetails", updatedUserDetails);
              }

              // Update the original request with new token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;

              // Process queued requests with new token
              processQueue(null, newToken);

              // Retry the original request
              return axiosInstance(originalRequest);
            }
          }

          // If refresh failed, clear and redirect
          throw new Error("Token refresh failed");
        } catch (error) {
          // Refresh failed - clear tokens and redirect to login
          processQueue(error, null);
          localStorage.removeItem("token");
          localStorage.removeItem("userDetails");
          window.location.href = "/";
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Queue the request while token is being refreshed
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((refreshErr) => {
            return Promise.reject(refreshErr);
          });
      }
    }

    return Promise.reject(err);
  },
);

export default axiosInstance;
